import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, getToken, setToken } from '@/lib/api';
import { waitForFirebaseAuth, getFirebaseAuth } from '@/lib/firebaseClient';

const AuthContext = createContext({
  user: null,
  company: null,
  ready: false,
  bootError: '',
  refresh: async () => null,
  logout: async () => {},
});

let bootInflight = null;

async function sleep(ms) {
  await new Promise((r) => setTimeout(r, ms));
}

export function AuthProvider({ children }) {
  const qc = useQueryClient();
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  // Always false until Firebase persistence is checked — avoids false logout on reload.
  const [ready, setReady] = useState(false);
  const [bootError, setBootError] = useState('');
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    const run = async () => {
      await waitForFirebaseAuth(8000);
      let auth = getFirebaseAuth();
      if (auth?.currentUser) {
        try {
          const fresh = await auth.currentUser.getIdToken(false);
          if (fresh) setToken(fresh);
        } catch {
          /* keep existing ls_token */
        }
      }

      let token = getToken();
      if (!token && !auth?.currentUser) {
        // One more persistence beat — IndexedDB restore can lag past the first callback.
        await sleep(400);
        await waitForFirebaseAuth(4000);
        auth = getFirebaseAuth();
        if (auth?.currentUser) {
          try {
            const fresh = await auth.currentUser.getIdToken(true);
            if (fresh) {
              setToken(fresh);
              token = fresh;
            }
          } catch {
            /* continue */
          }
        }
      }

      token = getToken();
      if (!token && !auth?.currentUser) {
        if (mounted.current) {
          setUser(null);
          setCompany(null);
          setBootError('');
          setReady(true);
        }
        return null;
      }

      const applyMe = (data) => {
        if (mounted.current) {
          setUser(data.user ?? null);
          setCompany(data.company ?? null);
          setBootError('');
          setReady(true);
        }
        qc.setQueryData(['me'], data);
        return data;
      };

      try {
        const r = await api('/api/v1/auth/me', { timeoutMs: 20000 });
        if (r.status === 200) return applyMe(r.data);

        if (r.status === 401 || r.status === 403) {
          auth = getFirebaseAuth();
          if (auth?.currentUser) {
            try {
              const forced = await auth.currentUser.getIdToken(true);
              if (forced) {
                setToken(forced);
                const retry = await api('/api/v1/auth/me', {
                  timeoutMs: 20000,
                  token: forced,
                });
                if (retry.status === 200) return applyMe(retry.data);
              }
            } catch {
              /* fall through */
            }
          } else {
            // Last chance restore before clearing session.
            await sleep(500);
            await waitForFirebaseAuth(4000);
            auth = getFirebaseAuth();
            if (auth?.currentUser) {
              try {
                const forced = await auth.currentUser.getIdToken(true);
                if (forced) {
                  setToken(forced);
                  const retry = await api('/api/v1/auth/me', {
                    timeoutMs: 20000,
                    token: forced,
                  });
                  if (retry.status === 200) return applyMe(retry.data);
                }
              } catch {
                /* fall through */
              }
            }
          }

          // Only clear token when Firebase also has no user — avoid wipe on transient 401.
          if (!getFirebaseAuth()?.currentUser) {
            setToken(null);
          }
          if (mounted.current) {
            setUser(null);
            setCompany(null);
            setBootError('');
            setReady(true);
          }
          return null;
        }

        if (mounted.current) {
          setBootError(String(r.data?.error || 'Could not load your session.'));
          setReady(true);
        }
        return null;
      } catch {
        if (mounted.current) {
          setBootError('API unreachable. Check your connection and try again.');
          setReady(true);
        }
        return null;
      }
    };

    if (bootInflight) return bootInflight;
    bootInflight = run().finally(() => {
      bootInflight = null;
    });
    return bootInflight;
  }, [qc]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api('/api/v1/auth/logout', { method: 'POST', body: {} });
    } catch {
      /* ignore */
    }
    setToken(null);
    setUser(null);
    setCompany(null);
    qc.clear();
  }, [qc]);

  const value = useMemo(
    () => ({ user, company, ready, bootError, refresh, logout }),
    [user, company, ready, bootError, refresh, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
